import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Leaf, Heart, Sparkles, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// 新しい診断の質問データ
const questions = [
  {
    question: "今の生活リズムに一番近いのは？",
    options: [
      "規則正しい生活を心がけている",
      "忙しくて不規則になりがち", 
      "自分のペースで過ごしている"
    ]
  },
  {
    question: "あなたが「幸せだな」と感じる瞬間は？",
    options: [
      "家族や友人と一緒にいるとき",
      "一人でリラックスしているとき",
      "何かを達成したとき"
    ]
  },
  {
    question: "最近、心や体で一番気になるのは？",
    options: [
      "頑張りすぎて疲れている",
      "人とのつながりが足りない",
      "先の方向性が見えない",
      "感情に振り回される",
      "満たされない感覚がある"
    ]
  },
  {
    question: "毎日のストレスレベルは？",
    options: [
      "高い。常にプレッシャーを感じている",
      "中くらい。時々ある",
      "低い。あまり感じない"
    ]
  },
  {
    question: "一日に自然と触れる時間はどのくらい？",
    options: [
      "ほとんどない",
      "少しはある",
      "よく自然に触れている"
    ]
  },
  {
    question: "自分を整える習慣は？",
    options: [
      "毎日している",
      "時々している",
      "ほとんどない"
    ]
  },
  {
    question: "休日の過ごし方に近いものは？",
    options: [
      "家でゆっくりと休む",
      "誰かと過ごす",
      "趣味や挑戦をたのしむ"
    ]
  },
  {
    question: "あなたの理想の一日に一番近いのは？",
    options: [
      "穏やかで静かな時間",
      "誰かとつながる時間",
      "成長や挑戦を感じる時間"
    ]
  },
  {
    question: "最近よく考えることは？",
    options: [
      "とにかく休みたい",
      "もっと人と話したい",
      "これから何をすべきか分からない",
      "気持ちが落ち着かない",
      "何をしても満たされない"
    ]
  },
  {
    question: "自分にとっての「幸せ」とは？",
    options: [
      "安心して過ごせること",
      "人とのつながり",
      "成長や達成",
      "こころの静けさ",
      "自然との調和"
    ]
  }
];

// 診断タイプの定義
const happinessTypes = {
  rest: {
    name: "リラックス重視タイプ",
    description: "心の平穏と安らぎを大切にするあなた。自分のペースを守ることが幸せの鍵です。",
    advice: "無理をせず、自分の心地よいリズムを大切にしてください。瞑想や自然との触れ合いがおすすめです。",
    color: "from-green-400 to-emerald-400"
  },
  connection: {
    name: "つながり重視タイプ", 
    description: "人との絆や関係性を大切にするあなた。温かいコミュニティが幸せの源です。",
    advice: "大切な人との時間を意識的に作り、深いつながりを育んでいきましょう。",
    color: "from-blue-400 to-cyan-400"
  },
  growth: {
    name: "成長重視タイプ",
    description: "挑戦と成長を通じて充実感を得るあなた。新しい経験が幸せをもたらします。",
    advice: "小さな目標を設定し、達成感を積み重ねることで充実した日々を送れるでしょう。",
    color: "from-purple-400 to-violet-400"
  },
  balance: {
    name: "バランス重視タイプ",
    description: "様々な要素のバランスを取ることで幸せを感じるあなた。調和が大切です。",
    advice: "仕事、休息、人間関係のバランスを意識し、偏りすぎないよう注意しましょう。",
    color: "from-orange-400 to-amber-400"
  },
  nature: {
    name: "自然調和タイプ",
    description: "自然との調和の中に幸せを見出すあなた。シンプルな生活が心を豊かにします。",
    advice: "自然に触れる時間を増やし、シンプルで丁寧な暮らしを心がけてみてください。",
    color: "from-teal-400 to-green-400"
  }
};

const HappinessCheck = () => {
  const [currentStep, setCurrentStep] = useState<'intro' | 'questions' | 'result'>('intro');
  const [email, setEmail] = useState('');
  const [answers, setAnswers] = useState<string[]>(new Array(questions.length).fill(''));
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [result, setResult] = useState<any>(null);
  const [isEmailSending, setIsEmailSending] = useState(false);
  const { toast } = useToast();

  const handleAnswerChange = (questionIndex: number, value: string) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = value;
    setAnswers(newAnswers);
  };

  const calculateResult = () => {
    // 回答パターンに基づいてタイプを判定
    let restScore = 0;
    let connectionScore = 0;
    let growthScore = 0;
    let balanceScore = 0;
    let natureScore = 0;

    answers.forEach((answer, index) => {
      switch (index) {
        case 0: // 生活リズム
          if (answer.includes('自分のペース')) restScore += 2;
          if (answer.includes('規則正しい')) balanceScore += 2;
          break;
        case 1: // 幸せを感じる瞬間
          if (answer.includes('家族や友人')) connectionScore += 3;
          if (answer.includes('一人でリラックス')) restScore += 3;
          if (answer.includes('達成')) growthScore += 3;
          break;
        case 2: // 気になること
          if (answer.includes('疲れている')) restScore += 2;
          if (answer.includes('つながりが足りない')) connectionScore += 2;
          if (answer.includes('方向性が見えない')) growthScore += 2;
          break;
        case 3: // ストレスレベル
          if (answer.includes('低い')) restScore += 2;
          if (answer.includes('高い')) balanceScore += 2;
          break;
        case 4: // 自然との触れ合い
          if (answer.includes('よく自然')) natureScore += 3;
          if (answer.includes('少しはある')) natureScore += 1;
          break;
        case 5: // 整える習慣
          if (answer.includes('毎日')) balanceScore += 2;
          if (answer.includes('時々')) balanceScore += 1;
          break;
        case 6: // 休日の過ごし方
          if (answer.includes('ゆっくりと休む')) restScore += 2;
          if (answer.includes('誰かと過ごす')) connectionScore += 2;
          if (answer.includes('趣味や挑戦')) growthScore += 2;
          break;
        case 7: // 理想の一日
          if (answer.includes('穏やかで静か')) restScore += 2;
          if (answer.includes('つながる時間')) connectionScore += 2;
          if (answer.includes('成長や挑戦')) growthScore += 2;
          break;
        case 8: // 最近よく考えること
          if (answer.includes('休みたい')) restScore += 2;
          if (answer.includes('人と話したい')) connectionScore += 2;
          if (answer.includes('何をすべきか')) growthScore += 2;
          break;
        case 9: // 幸せとは
          if (answer.includes('安心して過ごせる')) restScore += 2;
          if (answer.includes('人とのつながり')) connectionScore += 2;
          if (answer.includes('成長や達成')) growthScore += 2;
          if (answer.includes('こころの静けさ')) restScore += 1;
          if (answer.includes('自然との調和')) natureScore += 2;
          break;
      }
    });

    const scores = { rest: restScore, connection: connectionScore, growth: growthScore, balance: balanceScore, nature: natureScore };
    const maxScore = Math.max(...Object.values(scores));
    const dominantType = Object.keys(scores).find(key => scores[key as keyof typeof scores] === maxScore) as keyof typeof happinessTypes;

    return {
      type: dominantType,
      scores,
      totalScore: Object.values(scores).reduce((sum, score) => sum + score, 0)
    };
  };

  const saveResultToDatabase = async (diagnosisResult?: any) => {
    const resultToSend = diagnosisResult || result;
    if (!resultToSend || !email) {
      console.log('データベース保存スキップ: 結果またはメールアドレスが不足', { resultToSend: !!resultToSend, email });
      return;
    }
    
    console.log('=== 診断結果をデータベースに保存中 (簡単チェック) ===');
    
    setIsEmailSending(true);
    
    try {
      const typeInfo = happinessTypes[resultToSend.type];
      
      const { data, error } = await supabase
        .from('diagnosis_results_2025_11_12_13_00')
        .insert({
          email: email,
          diagnosis_type: 'happiness_check',
          result_type: resultToSend.type,
          scores: resultToSend.scores,
          total_score: resultToSend.totalScore,
          type_info: typeInfo
        });
      
      if (error) {
        console.error('データベース保存エラー:', error);
        throw error;
      }
      
      console.log('診断結果をデータベースに保存しました:', data);
      
      toast({
        title: "診断結果が保存されました",
        description: "診断が完了し、結果が保存されました。",
      });
      
    } catch (error) {
      console.error('データベース保存エラー:', error);
      
      toast({
        title: "診断結果が表示されました",
        description: "診断が完了しました。結果をご確認ください。",
      });
    } finally {
      setIsEmailSending(false);
      console.log('=== 診断処理終了 (簡単チェック) ===');
    }
  };

  const handleSubmit = async () => {
    if (answers.every(answer => answer !== '')) {
      const diagnosisResult = calculateResult();
      setResult(diagnosisResult);
      setCurrentStep('result');
      
      // 自動的にデータベースに保存
      await saveResultToDatabase(diagnosisResult);
    }
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  if (currentStep === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/30 to-accent/20 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl shadow-zen">
          <CardHeader className="text-center space-y-6">
            <div className="flex justify-center items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  戻る
                </Button>
              </Link>
              <div className="p-4 bg-gradient-to-br from-primary/10 to-accent/10 rounded-full">
                <Heart className="w-12 h-12 text-primary" />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold bg-gradient-zen bg-clip-text text-transparent">
              北欧しあわせ体質チェック
            </CardTitle>
            <p className="text-lg text-muted-foreground leading-relaxed">
              あなたのしあわせタイプを診断いたします。<br />
              今のあなたにピッタリのヒントをお届けいたします。<br />
              所要時間は5分程度です。
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-base font-medium">メールアドレス *</Label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2"
                required
              />
            </div>
            <Button 
              onClick={() => setCurrentStep('questions')} 
              disabled={!email}
              className="w-full bg-gradient-zen hover:opacity-90 transition-zen"
              size="lg"
            >
              診断を開始する
              <Sparkles className="ml-2 w-5 h-5" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (currentStep === 'questions') {
    const currentQ = questions[currentQuestion];
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/30 to-accent/20 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl shadow-zen">
          <CardHeader>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl">質問 {currentQuestion + 1} / {questions.length}</CardTitle>
                <span className="text-sm text-muted-foreground">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </CardHeader>
          <CardContent className="space-y-8">
            <div className="text-center">
              <h3 className="text-lg font-medium leading-relaxed mb-6">
                {currentQ.question}
              </h3>
              <RadioGroup
                value={answers[currentQuestion]}
                onValueChange={(value) => handleAnswerChange(currentQuestion, value)}
                className="space-y-4"
              >
                {currentQ.options.map((option, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                    <RadioGroupItem value={option} id={`q${currentQuestion}-${index}`} />
                    <Label htmlFor={`q${currentQuestion}-${index}`} className="flex-1 text-left cursor-pointer">
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            
            <div className="flex justify-between">
              <Button 
                onClick={prevQuestion} 
                disabled={currentQuestion === 0}
                variant="outline"
              >
                前の質問
              </Button>
              
              {currentQuestion === questions.length - 1 ? (
                <Button 
                  onClick={handleSubmit}
                  disabled={answers[currentQuestion] === ''}
                  className="bg-gradient-zen hover:opacity-90 transition-zen"
                >
                  診断結果を見る
                  <Heart className="ml-2 w-4 h-4" />
                </Button>
              ) : (
                <Button 
                  onClick={nextQuestion}
                  disabled={answers[currentQuestion] === ''}
                  className="bg-gradient-zen hover:opacity-90 transition-zen"
                >
                  次の質問
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (currentStep === 'result' && result) {
    const typeInfo = happinessTypes[result.type];
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/30 to-accent/20 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl shadow-zen">
          <CardHeader className="text-center space-y-6">
            <div className="flex justify-center">
              <div className={`p-6 bg-gradient-to-br ${typeInfo.color} rounded-full`}>
                <Heart className="w-12 h-12 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">
              あなたは「{typeInfo.name}」です
            </CardTitle>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {typeInfo.description}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted/50 p-6 rounded-lg">
              <h4 className="font-semibold mb-3">あなたへのアドバイス</h4>
              <p className="text-muted-foreground leading-relaxed">
                {typeInfo.advice}
              </p>
            </div>
            
            <div className="space-y-4">
              <h4 className="font-semibold">詳細スコア</h4>
              {Object.entries(result.scores).map(([key, score]) => {
                const type = happinessTypes[key as keyof typeof happinessTypes];
                const maxPossible = 10; // 概算の最大スコア
                const percentage = Math.min((score / maxPossible) * 100, 100);
                return (
                  <div key={key} className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>{type.name}</span>
                      <span>{score}pt</span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                  </div>
                );
              })}
            </div>
            
            <div className="flex justify-center">
              <Button 
                onClick={() => {
                  setCurrentStep('intro');
                  setAnswers(new Array(questions.length).fill(''));
                  setCurrentQuestion(0);
                  setResult(null);
                }}
                variant="outline"
                className="px-8"
              >
                もう一度診断する
              </Button>

            </div>

            <div className="text-center space-y-4">
              <Link to="/">
                <Button variant="ghost">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  メニューに戻る
                </Button>
              </Link>
              
              {/* 外部サイトへの戻るボタン */}
              <div className="pt-4 border-t border-muted">
                <a 
                  href="https://hokuou-wellbeing.jp/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="bg-gradient-zen hover:opacity-90 transition-zen text-white border-none"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    北欧ウェルビーイングに戻る
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default HappinessCheck;